import { takeLatest, put, debounce, select, call, all } from 'redux-saga/effects';
import { get, isEmpty } from 'lodash';
import { message } from 'antd';
import { getFilterFromRules, getRulesFromFilter } from '@utils';
import { PUSH_DRILLDOWN_FILTER } from '@store/dimensionContainer/types';
import * as filterContainerTypes from '@store/filterContainer/types';
import { selectedMetricChart, setMetrics } from '@store/metricsChartContainer/actions';
import {
  setDimensionSortByMetric,
  setDimensionsFromDimensionList,
} from '@store/dimensionTables/actions';
import { metricSelector, selectedMetricSelector } from '@store/metricsChartContainer/selectors';
import { setPersistedData } from '@utils/persistenceUtil';
import { DEFAULT_CHART_METRICS, METRICS_TYPES_EXCLUDE_BY_FILTER } from '@utils/metrics';
import shortId from '@utils/shortId';
import { findDimensionDetailsInFilter } from '@src/utils/filter/getDimensionTypesFromFilter';
import { setSummaryWidgets } from '@store/summaryWidgets/actions';
import { getDimensionMapping } from '@src/api/aiAlerts';
import {
  isAppModule,
  DEFAULT_METRIC,
  LAYOUT_MAP,
  AD_PLAYS,
  MARIO,
  isEcoServerModule,
} from '@utils/constants';
import { globalOrderBySelector } from '@store/trends/base/selectors';
import { preserveOrder } from '@components/common/DnD/utils';
import { SUMMARY_PANEL_TYPE } from '@components/top-widget-panel/constants';
import { allowedGranularities } from '@components/common/GranularitySelect/utils';
import { SPI_METRIC } from '@components/top-widget-panel/spi-panel/widgets/constants';
import * as summaryWidgetsTypes from '@store/summaryWidgets/types';
import * as metricsChartContainerTypes from '@store/metricsChartContainer/types';
import * as dimensionListTypes from './dimensionList/types';
import * as dimensionTablesTypes from '@store/dimensionTables/types';
import * as trendsBaseTypes from '@store/trends/base/types';
import { summaryWidgetsSelector } from '@store/summaryWidgets/selector';
import { initialState } from '@store/summaryWidgets/reducer';
import { FIXED_APP_FILTERS } from '@src/components/filter-panel/constants';
import {
  featureSelector,
  appAIAlertFeatureFlagSelector,
  aiAlertsPCPFeatureSelector,
  aiAlertsCIRRFeatureSelector,
  bleEnabledSelector,
  aiAlertsSensitivitySelector,
  getFirstAppliedFilter,
  supportedMetricsSelector,
  metricsByTypesSelector,
  filterSelector,
  currentMetricsMetaSelector,
  intervalSelector,
} from './selectors';
import { getAIAlertsMetricsMetadata } from '@utils/aiAlerts/metrics';
import {
  setDimensions,
  setGlobalOrderByMetric,
  setDimensionMetrics,
} from '@store/trends/base/actions';
import * as commonTypes from './types';
import {
  applyFilter,
  persistState,
  toggleLayout,
  updateGranularity,
  setAppBackendType,
  setAiAlertsMetricsMeta,
} from './actions';
import { dataToPersistSelector, parseMetricsFn } from './persistence';
import {
  dimensionByTypesSelector,
  dimensionsListSelectorKeyValue,
} from './dimensionList/selectors';

import { getDefaultConfig, validPresetKeys } from '@src/components/common/Dashboard/UseCases/utils';

function* handlePersistedState(action) {
  // persistence action is common
  // ignore if triggered from another tab
  const key = get(action, ['response', 'key'], null);

  if (!['/', '/app-experience'].includes(key)) {
    return;
  }

  const configuration = get(action, ['response', 'configuration'], {});
  yield applyState(configuration, action);
}

function* handlePersistedStateForPreset(action) {
  // persistence action for preset dashboards
  const key = get(action, ['response', 'key'], null);
  const presetKey = key.substr(8);
  if (!validPresetKeys.includes(presetKey)) {
    return;
  }

  const supportedMetrics = yield select(supportedMetricsSelector);
  const dimensions = yield select(dimensionsListSelectorKeyValue);
  const aiAlertsSensitivities = yield select(aiAlertsSensitivitySelector);

  const defaultConfig = getDefaultConfig(
    supportedMetrics,
    dimensions,
    presetKey,
    aiAlertsSensitivities,
  );

  const configuration = get(action, ['response', 'configuration'], defaultConfig);
  yield applyState(configuration, action);
}

function* applyState(configuration, action) {
  const { meta = {} } = action;
  const supportedMetrics = yield select(supportedMetricsSelector);
  const summaryWidgets = yield select(summaryWidgetsSelector);
  const parseMetrics = parseMetricsFn(supportedMetrics);

  // moved from persistence config saga to keep it generic
  const persistentFilter = get(configuration, ['filter'], null);
  if (persistentFilter) {
    const tRules = getRulesFromFilter(persistentFilter.rule, true);
    const tFilter = getFilterFromRules(tRules, true);

    yield put({
      type: filterContainerTypes.RESET_DRILL_DOWN,
    });
    yield put({
      type: PUSH_DRILLDOWN_FILTER,
      name: tFilter.name,
      rule: tFilter.rule,
      presetFid: persistentFilter.presetFid || null,
    });
    yield setPersistedData(applyFilter(persistentFilter));
  }

  const granularity = get(configuration, ['granularity'], null);
  if (granularity) {
    const interval = yield select(intervalSelector);
    const allowed = allowedGranularities(interval);
    if (allowed.includes(granularity)) {
      yield setPersistedData(updateGranularity(granularity));
    }
  }

  // summary widgets
  // { type: '', widgets: [{ widgetKey: '', config: {}}] }
  const persistedSummaryWidgets = get(configuration, ['summaryWidgets'], summaryWidgets);
  if (Array.isArray(persistedSummaryWidgets)) {
    // HP-4563 hacky fix for App SPI Widgets
    if (isAppModule && persistedSummaryWidgets[0].config.metrics[0] !== SPI_METRIC) {
      yield setPersistedData(setSummaryWidgets(summaryWidgets));
    } else {
      yield setPersistedData(
        setSummaryWidgets({
          type: SUMMARY_PANEL_TYPE.SPI,
          widgets: persistedSummaryWidgets,
        }),
      );
    }
  } else {
    if (
      isAppModule &&
      persistedSummaryWidgets.type &&
      persistedSummaryWidgets.type === SUMMARY_PANEL_TYPE.NETWORK
    ) {
      yield setPersistedData(setSummaryWidgets(persistedSummaryWidgets));
    } else {
      yield setPersistedData(setSummaryWidgets(initialState));
    }
  }

  // chart metrics
  const chartMetrics = get(configuration, ['chartMetrics'], DEFAULT_CHART_METRICS);
  yield setPersistedData(setMetrics(parseMetrics(chartMetrics)));

  // selected metric
  const selectedMetric = get(configuration, ['selectedMetric'], DEFAULT_METRIC);
  const [parsedSelectedMetric = DEFAULT_METRIC] = parseMetrics([selectedMetric]);
  yield setPersistedData(selectedMetricChart(parsedSelectedMetric));

  // order by metric
  const dimensionSortByMetric = get(configuration, ['dimensionSortByMetric'], DEFAULT_METRIC);
  const [parsedDimensionSortByMetric = DEFAULT_METRIC] = parseMetrics([dimensionSortByMetric]);
  yield setPersistedData(setDimensionSortByMetric(parsedDimensionSortByMetric));

  // dimensions
  // changed dimension get filtered out by getDimensionNamesByMetric fn on mount
  const dimensions = get(configuration, ['dimensions'], null);
  if (dimensions !== null) {
    yield setPersistedData(setDimensionsFromDimensionList(dimensions));
  }

  // order is important! dimensionMetrics needs to be set before setDimensions is called
  // new trends: dimension metrics
  const dimensionMetrics = get(configuration, ['dimensionMetrics'], null);
  if (dimensionMetrics !== null) {
    yield setPersistedData(setDimensionMetrics(parseMetrics(dimensionMetrics)));
  }

  // new trends: dimension table state
  const dimensionKeys = get(configuration, ['dimensionKeys'], null);
  if (dimensionKeys !== null) {
    yield setPersistedData(setDimensions(dimensionKeys));
  }

  // This can be removed by now. but just in case if there is anyone who still had old backend this function will reset
  // their backend to mario defaults
  const appBackendType = get(configuration, ['appBackendType'], null);
  if (isAppModule && appBackendType && appBackendType !== MARIO) {
    const allTraffic = FIXED_APP_FILTERS[0];
    const tRules = getRulesFromFilter(allTraffic.rule, true);
    const tFilter = getFilterFromRules(tRules, true);
    yield all([
      put({
        type: filterContainerTypes.RESET_DRILL_DOWN,
      }),
      put({
        type: PUSH_DRILLDOWN_FILTER,
        name: tFilter.name,
        rule: tFilter.rule,
        presetFid: null,
      }),
      setPersistedData(setAppBackendType(MARIO)),
      setPersistedData(selectedMetricChart(DEFAULT_METRIC)),
      setPersistedData(setDimensionSortByMetric(DEFAULT_METRIC)),
      setPersistedData(setMetrics(parseMetrics(DEFAULT_CHART_METRICS))),
      setPersistedData(setGlobalOrderByMetric(DEFAULT_METRIC)),
      setPersistedData(applyFilter(allTraffic)),
    ]);
  }

  // new trends: global order by
  const globalOrderBy = get(configuration, ['globalOrderBy'], null);
  if (globalOrderBy !== null) {
    const [parsedGlobalOrderBy = parsedSelectedMetric] = parseMetrics([globalOrderBy]);
    yield setPersistedData(setGlobalOrderByMetric(parsedGlobalOrderBy));
  }

  // ignore dod persistence

  // ignore persistence and force new layout on load
  // remove `-ignore` form key to restore persistence behaviour
  // this code can be removed when old layout is depericated
  const layout = get(configuration, ['layout-ignore'], LAYOUT_MAP.horizontal);
  yield setPersistedData(toggleLayout(layout));

  // let caller know persistence is loaded and applied
  if ('doneCallback' in meta) {
    meta.doneCallback();
  }
}

function* watchPersistedState() {
  yield takeLatest([commonTypes.UPDATE_PERSISTED_COMMON_STATE], handlePersistedState);
}

function* watchPersistedStateForPreset() {
  yield takeLatest(
    [commonTypes.UPDATE_PERSISTED_COMMON_STATE_FOR_PRESET],
    handlePersistedStateForPreset,
  );
}

function* handlePersistence(action) {
  // TODO: what if first action did not have skip but
  // the most recent one in debounce did?
  if (!action.skipPersistence) {
    const configuration = yield select(dataToPersistSelector);
    yield put(persistState(configuration));
  }
}

function* handleCommonApplyFilter() {
  const filter = yield select(filterSelector);
  const selectedMetrics = yield select(metricSelector);
  const currentMetricsMeta = yield select(currentMetricsMetaSelector);
  const selectedChartMetric = yield select(selectedMetricSelector);
  const metricsByTypes = yield select(metricsByTypesSelector);
  const dimensionByTypes = yield select(dimensionByTypesSelector);
  const globalOrderBy = yield select(globalOrderBySelector);

  if (!isEmpty(dimensionByTypes) && !isEcoServerModule) {
    const { dimensionTypes } = findDimensionDetailsInFilter(filter, dimensionByTypes);
    let excludedMetricTypes: string[] = [];
    if (isAppModule) {
      dimensionTypes.forEach((tag) => {
        excludedMetricTypes = [...excludedMetricTypes, ...METRICS_TYPES_EXCLUDE_BY_FILTER[tag]];
      });
    }
    const uniqueExcludeMetricTypes = [...new Set(excludedMetricTypes)];
    /*
    For EI, filterdMetrics here, will be all EI metrics if applied filter has ad dimensions.
    For AI, there are no such mapping done.
    For APP, filteredMetrics is calculated based on the filter and mapping done in utils/metricList
    */
    const filteredMetrics = Object.keys(currentMetricsMeta).filter((metric) => {
      const metricType = metricsByTypes[metric];
      return uniqueExcludeMetricTypes.includes(metricType);
    });
    if (isAppModule) {
      if (filteredMetrics.includes(globalOrderBy)) {
        yield put(setGlobalOrderByMetric(DEFAULT_METRIC));
      }
      if (filteredMetrics.includes(selectedChartMetric)) {
        yield put(selectedMetricChart(DEFAULT_METRIC, null));
        yield put(setMetrics(preserveOrder(selectedMetrics, [...selectedMetrics, DEFAULT_METRIC])));
      }
    } else {
      if (filteredMetrics.includes(globalOrderBy)) {
        yield put(setGlobalOrderByMetric(AD_PLAYS));
      }
      if (filteredMetrics.includes(selectedChartMetric)) {
        yield put(selectedMetricChart(AD_PLAYS, null));
        yield put(setMetrics(preserveOrder(selectedMetrics, [...selectedMetrics, AD_PLAYS])));
      }
    }
  }
}

function* watchPersistence() {
  yield debounce(
    300,
    [
      commonTypes.APPLY_FILTER, // filter
      summaryWidgetsTypes.SET_SUMMARY_WIDGETS, // summary widgets
      metricsChartContainerTypes.SET_METRICS, // chart metrics
      metricsChartContainerTypes.SELECTED_METRIC_CHART, // selected metric
      dimensionTablesTypes.SET_DIMENSION_SORT_BY_METRIC, // order by metric
      dimensionTablesTypes.CHANGE_DIMENSION, // dimension change
      dimensionTablesTypes.CHANGE_DIMENSION_SORT, // dimension position swap
      commonTypes.TOGGLE_LAYOUT, // trends panel layout
      commonTypes.UPDATE_GRANULARITY, // timeseries granularity

      // new dimension table types
      trendsBaseTypes.SWAP_SELECTED_DIMENSION,
      trendsBaseTypes.SET_SELECTED_DIMENSION,
      trendsBaseTypes.ADD_DIMENSION,
      trendsBaseTypes.REMOVE_DIMENSION,
      trendsBaseTypes.SET_DIMENSIONS,
      trendsBaseTypes.SET_GLOBAL_ORDER_BY,
      trendsBaseTypes.SET_DIMENSION_METRICS,
    ],
    handlePersistence,
  );
}

function* watchCommonApplyFilter() {
  yield takeLatest(
    [
      commonTypes.APPLY_FILTER, // filter
      dimensionListTypes.FETCHING_SUCCESS, // dimension list
      metricsChartContainerTypes.SET_METRICS,
    ],
    handleCommonApplyFilter,
  );
}

function* prepareAiAlertsMetricsMeta() {
  const sensitivity = yield select(aiAlertsSensitivitySelector);
  const isAppAlertEnabled = yield select(appAIAlertFeatureFlagSelector);
  const isPcpEnabled = yield select(aiAlertsPCPFeatureSelector);
  const isBusinessMetricsEnabled = yield select(bleEnabledSelector);
  const isAlertPartnerAccount = yield select(featureSelector('is_alert_forwarding_partner'));
  const isContentAlertsEnabled = yield select(featureSelector('ai_alerts')) ||
    isAlertPartnerAccount;
  const isAdAlertsEnabled = yield select(featureSelector('ad_ai_alerts'));
  const featureFlags = {
    isAdAlertsEnabled,
    isContentAlertsEnabled,
    isAppAlertEnabled,
    isPcpEnabled,
    isBusinessMetricsEnabled,
  };
  const metricList = sensitivity.map((s) => s.metric);
  const aiAlertsMetricMeta = getAIAlertsMetricsMetadata(featureFlags, metricList);
  yield put(setAiAlertsMetricsMeta(aiAlertsMetricMeta));
}

// triggers apply filter action to reload all trends queries
function* handleRefreshTrends() {
  // trigger reload of all trends queries
  // by applying the same filter with a random key
  const appliedFilter = yield select(getFirstAppliedFilter);

  // a radom string is added to trigger refresh (_rKey)
  const refreshKey = shortId();
  yield put(applyFilter({ ...appliedFilter, _rKey: refreshKey }));
}

function* watchRefreshTrends() {
  yield takeLatest([commonTypes.REFRESH_TRENDS], handleRefreshTrends);
}

export function* fetchDimensionMapping() {
  try {
    const response = yield call(getDimensionMapping);
    yield put({
      type: commonTypes.AI_ALERTS_DIMENSION_MAPPING,
      mapData: response.data,
    });
  } catch (error: any) {
    yield call(message.error, error.message);
    throw error;
  }
}

function* watchAiAlertsSensitivity() {
  yield takeLatest([commonTypes.SET_AI_ALERTS_SENSITIVITY], prepareAiAlertsMetricsMeta);
}

export default [
  watchPersistedState,
  watchCommonApplyFilter,
  watchPersistence,
  watchRefreshTrends,
  fetchDimensionMapping,
  watchAiAlertsSensitivity,
  watchPersistedStateForPreset,
];
